// src/Pages/LegalHostTerms.jsx — Full Host Terms, styled, MO-governed, nationwide operations.
// - When the user scrolls to the bottom, we:
//   1) set localStorage "hostTermsRead_v1" = "1"
//   2) postMessage({ type: "host_terms_read_v1" }, window.location.origin)
//   3) if embedded/opened from Account, parent/opener can listen for that message.
// - Supports optional ?return=/account#host-accept to ease navigation back.
// - TailwindCSS styling with a clean “Airbnb-grade” layout.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

const EFFECTIVE_DATE = "2025-10-17";
const COMPANY_NAME = `J Group, LLC d/b/a "You're Welcome"`;
const BRAND = `You're Welcome`;

export default function LegalHostTerms() {
  const location = useLocation();
  const [atEnd, setAtEnd] = useState(false);
  const didSignalRef = useRef(false);

  // Parse ?return=... for convenience (optional)
  const returnHref = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    const r = qs.get("return");
    return r && r.startsWith("/") ? r : null;
  }, [location.search]);

  // Scroll listener on window (page-level detection)
  useEffect(() => {
    function maybeSignal() {
      if (didSignalRef.current) return;
      const doc = document.documentElement || document.body;
      const nearBottom =
        window.innerHeight + window.scrollY >= doc.scrollHeight - 8;
      if (nearBottom) {
        didSignalRef.current = true;
        setAtEnd(true);
        try {
          localStorage.setItem("hostTermsRead_v1", "1");
        } catch {
          // ignore storage errors
        }
        try {
          const payload = { type: "host_terms_read_v1" };
          window.opener?.postMessage(payload, window.location.origin);
          if (window.parent !== window) {
            window.parent.postMessage(payload, window.location.origin);
          }
        } catch {
          // ignore cross-window errors
        }
      }
    }
    maybeSignal();
    window.addEventListener("scroll", maybeSignal, { passive: true });
    return () => window.removeEventListener("scroll", maybeSignal);
  }, []);

  return (
    <div className="mx-auto max-w-6xl px-4 py-10">
      {/* Hero Header */}
      <header className="mb-10 border-b border-neutral-100 pb-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500">
              Host Legal Terms
            </p>
            <h1 className="mt-1 text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl">
              {BRAND} — Host Terms &amp; Conditions
            </h1>
            <p className="mt-1 text-sm text-neutral-600">
              Effective: {EFFECTIVE_DATE}
            </p>
            <p className="mt-2 text-[13px] leading-relaxed text-neutral-500">
              These Host Terms are provided by {COMPANY_NAME} (
              <span>“</span>
              <strong>{BRAND}</strong>
              <span>”</span>), based in Missouri and operating nationwide. They
              are written to be readable but are legally binding. Please review
              them carefully before enabling hosting.
            </p>
          </div>

          {/* Optional helper banner */}
          {returnHref && (
            <div className="mt-4 rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-xs text-neutral-700 shadow-sm sm:mt-0 sm:max-w-sm">
              <p className="font-semibold text-neutral-800">
                Reading these for hosting?
              </p>
              <p className="mt-1">
                After you scroll to the bottom, return to{" "}
                <a
                  className="font-medium text-[#7C9082] underline underline-offset-2 hover:text-[#6C7F72]"
                  href={returnHref}
                >
                  your account
                </a>{" "}
                to confirm and enable hosting.
              </p>
            </div>
          )}
        </div>
      </header>

      {/* Layout: sticky TOC + content */}
      <div className="grid gap-8 lg:grid-cols-[260px_minmax(0,1fr)]">
        {/* TOC */}
        <aside className="h-max lg:sticky lg:top-6">
          <nav
            aria-label="On this page"
            className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm"
          >
            <div className="text-xs font-semibold uppercase tracking-wide text-neutral-700">
              On this page
            </div>
            <ul className="mt-3 space-y-2 text-sm">
              {[
                ["agreement", "1. Agreement & Definitions"],
                ["role", "2. Platform Role; Independent Contractor"],
                ["eligibility", "3. Eligibility & Authority"],
                ["listings", "4. Listings & Standards"],
                ["permits", "5. Compliance, Permits & Local Rules"],
                ["taxes", "6. Taxes & Registrations"],
                ["pricing", "7. Pricing, Fees & Payouts"],
                ["bookings", "8. Bookings, Cancels & Off-Platform"],
                ["access", "9. Access, Condition, Safety & Devices"],
                ["damage", "10. Damage, Deposits, Claims & Costs"],
                ["insurance", "11. Insurance & Waiver of Subrogation"],
                ["risk", "12. Risk Allocation; Release & Indemnity"],
                ["checks", "13. KYC, Background & Compliance Checks"],
                ["content", "14. Reviews & Content License"],
                ["data", "15. Data, Communications & E-Sign"],
                ["suspension", "16. Suspension & Termination"],
                ["changes", "17. Modifications"],
                ["law", "18. Governing Law (MO); Arbitration"],
                ["misc", "19. Miscellaneous"],
                ["contact", "20. Contact"],
              ].map(([id, label]) => (
                <li key={id}>
                  <a
                    href={`#${id}`}
                    className="text-neutral-700 underline underline-offset-2 transition-colors hover:text-[#7C9082]"
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        {/* Legal Content */}
        <main className="prose prose-neutral max-w-none">
          <section id="agreement" data-anchor>
            <h2>1. Agreement &amp; Definitions</h2>
            <p>
              These Host Terms &amp; Conditions (
              <strong>“Host Terms”</strong>) are a binding agreement between
              you (<strong>“Host”</strong>, including any entity you represent)
              and {COMPANY_NAME} (
              <span>“</span>
              <strong>{BRAND}</strong>
              <span>,” “we,” “our,” “us”</span>). They govern your listing and
              provision of any <strong>space</strong> (for example, a home,
              venue, studio, or office) and any <strong>hosting activities</strong>, including{" "}
              <strong>events</strong> and <strong>overnight stays</strong>,
              offered via the {BRAND} platform (the{" "}
              <strong>“Platform”</strong>).
            </p>
            <p>
              These Host Terms supplement {BRAND}’s{" "}
              <Link to="/legal/terms" className="underline">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/legal/privacy" className="underline">
                Privacy Policy
              </Link>{" "}
              (collectively, the <strong>“Platform Terms”</strong>). If there is
              a conflict, these Host Terms control for hosting matters.
            </p>
          </section>

          <section id="role" data-anchor>
            <h2>2. Platform Role; Independent Contractor</h2>
            <ul>
              <li>
                <strong>{BRAND} is a marketplace only.</strong> We connect Hosts
                and guests. We do not own, control, operate, or manage Host
                spaces and are not a landlord, property manager, hotel, broker,
                insurer, or legal advisor.
              </li>
              <li>
                <strong>Independent Host.</strong> You act as an independent
                contractor, not as our employee, partner, or agent. You have no
                authority to bind {BRAND}.
              </li>
              <li>
                <strong>No guarantee of bookings or earnings.</strong> We do not
                guarantee any level of demand, occupancy, or revenue.
              </li>
              <li>
                <strong>No inspection or certification.</strong> {BRAND} does
                not inspect or certify habitability, code compliance,
                accessibility, or suitability of your space.
              </li>
            </ul>
          </section>

          <section id="eligibility" data-anchor>
            <h2>3. Eligibility &amp; Authority</h2>
            <ul>
              <li>
                You must be able to form a binding contract and comply with all
                laws that apply to you and your listing.
              </li>
              <li>
                <strong>Authority.</strong> You represent and warrant that you
                own the space or have all required rights and consents (from
                owners, landlords, HOAs, condo boards, lenders, etc.) to host,
                to grant guests temporary access or occupancy, and to enforce
                house rules.
              </li>
              <li>
                You are responsible for your account, your listings, and all
                activity under your account.
              </li>
            </ul>
          </section>

          <section id="listings" data-anchor>
            <h2>4. Listings &amp; Standards</h2>
            <ul>
              <li>
                <strong>Accuracy.</strong> Listing details (description,
                photos, amenities, capacity, pricing, rules, availability) must
                be accurate and kept reasonably up to date.
              </li>
              <li>
                <strong>Condition.</strong> You will deliver the space in
                substantially the condition portrayed in the listing, clean and
                in good repair, with essential systems (e.g., plumbing, heating,
                cooling, electrical) in safe working order.
              </li>
              <li>
                <strong>Safety.</strong> You will provide and maintain required
                safety features (for example, smoke and CO detectors where
                required, fire extinguishers, egress paths, emergency
                information).
              </li>
              <li>
                <strong>House rules.</strong> You will set clear house rules
                consistent with applicable law, including expectations for noise,
                maximum occupancy, parking, smoking, pets, alcohol, and neighbor
                relations.
              </li>
              <li>
                <strong>Prohibited uses.</strong> You may not permit illegal,
                dangerous, discriminatory, infringing, or policy-violating
                activities at the space.
              </li>
            </ul>
          </section>

          <section id="permits" data-anchor>
            <h2>5. Compliance, Permits &amp; Local Rules (Host-Only Responsibility)</h2>
            <p>
              <strong>
                You, not {BRAND}, are solely responsible for legal compliance
                relating to your space and hosting.
              </strong>{" "}
              This includes everywhere your listing is located, whether in St.
              Louis or any other jurisdiction.
            </p>
            <p>
              Your responsibilities include, without limitation, determining and
              complying with:
            </p>
            <ul>
              <li>
                Short-term rental registrations, licensing, notice, and renewal
                requirements;
              </li>
              <li>
                Zoning, land use, occupancy limits, building codes, and
                fire/life-safety rules;
              </li>
              <li>
                Event and assembly rules (crowd capacity, egress, security,
                amplified sound, alcohol service, food handling);
              </li>
              <li>
                Health, sanitation, nuisance and noise ordinances, and quiet
                hours;
              </li>
              <li>
                Landlord, HOA, condo, or co-op rules and any required consents;
              </li>
              <li>
                Fair housing, public accommodations, non-discrimination, and
                anti-harassment laws;
              </li>
              <li>
                Accessibility and ADA obligations where applicable; and
              </li>
              <li>
                Any local transient, hotel, lodging, room occupancy, or similar
                regulations that apply to you as Host.
              </li>
            </ul>
            <p>
              You will obtain, maintain, and pay for all applicable permits,
              licenses, registrations, inspections, and approvals at your own
              expense and will provide proof to {BRAND} upon request. Failure to
              comply may result in cancellation of bookings, listing removal,
              account action, and your responsibility for any resulting fines,
              penalties, or costs (including those asserted against {BRAND} due
              to your non-compliance).
            </p>
          </section>

          <section id="taxes" data-anchor>
            <h2>6. Taxes &amp; Registrations</h2>
            <p className="text-[0.9em] font-semibold text-neutral-800">
              Summary: By default, you are responsible for your own tax
              obligations as a Host. In any jurisdiction where {BRAND} does not
              explicitly say that it collects and remits a specific tax on your
              behalf, <strong>you</strong> are the party responsible for
              handling that tax.
            </p>
            <ul>
              <li>
                <strong>Host tax responsibility (baseline rule).</strong> Except
                where we expressly state in writing (for example, in a help
                article, product surface, or jurisdiction-specific notice) that{" "}
                <strong>{BRAND}</strong> collects and remits a particular tax
                for a particular location,{" "}
                <strong>
                  you are solely responsible for assessing, collecting (where
                  applicable), reporting, and remitting all taxes related to
                  your hosting
                </strong>
                . This includes, by way of example only: sales and use taxes,
                value-added taxes (VAT/GST), income taxes, and any hotel,
                lodging, transient occupancy, or room-night taxes that apply to
                you as Host.
              </li>
              <li>
                <strong>When {BRAND} collects tax.</strong> In some locations,
                law may treat {BRAND} (or its payment partners) as a
                “marketplace facilitator” or similar, or we may voluntarily
                agree to collect and remit a given tax. If and when that
                happens, we will show the tax as a separate line item or clearly
                indicate that tax is being collected on the guest’s checkout.
                For those specific taxes and locations,{" "}
                <strong>
                  we will collect from the guest and remit, and you authorize us
                  to do so
                </strong>
                . You remain responsible for all other taxes that apply to you
                (for example, income tax on your payouts).
              </li>
              <li>
                <strong>Embedding tax in your price.</strong> In jurisdictions
                where {BRAND} is not collecting tax on your behalf, you
                understand and agree that guests may see only a single price
                (your price plus our platform fees), with no separate “tax”
                line. Where you are legally required to charge tax but {BRAND} is
                not collecting it for you,{" "}
                <strong>
                  you are responsible for setting your prices at a level that
                  allows you to satisfy your tax obligations from your payouts
                </strong>
                .
              </li>
              <li>
                <strong>Registration, forms &amp; reporting.</strong> You agree
                to provide any required tax forms (for example, W-9, W-8BEN),
                complete any KYC or beneficial-owner checks, and accept any
                required backup withholding. We may file informational reports
                (for example, IRS Form 1099-K or equivalent) as required by law.
              </li>
              <li>
                <strong>No tax advice.</strong> {BRAND} does not provide tax,
                legal, or accounting advice. You should consult your own
                advisors to understand how taxes, nexus thresholds, and
                marketplace-facilitator rules apply to you and your listings.
              </li>
            </ul>
          </section>

          <section id="pricing" data-anchor>
            <h2>7. Pricing, Fees &amp; Payouts</h2>
            <ul>
              <li>
                <strong>Host pricing.</strong> You control your base rates and
                reasonable listing-specific fees (for example, cleaning fees or
                security deposits), subject to Platform constraints. You are
                responsible for ensuring that your pricing reflects any taxes,
                costs, and margins you require.
              </li>
              <li>
                <strong>Platform and service fees.</strong> We may charge Host
                and/or guest service fees per booking to operate the Platform,
                process payments, and support trust and safety features. These
                are deducted from payouts or added to guest charges as
                disclosed.
              </li>
              <li>
                <strong>Payouts.</strong> Unless otherwise stated, payouts are
                generally initiated after guest check-in and may take additional
                time to reach your account depending on your payout provider.
              </li>
              <li>
                <strong>Offsets &amp; adjustments.</strong> We may withhold,
                reverse, or offset payouts to cover refunds, chargebacks,
                adjustments, fines, or other amounts you owe us, including
                enforcement and collection costs.
              </li>
            </ul>
          </section>

          <section id="bookings" data-anchor>
            <h2>8. Bookings, Cancellations &amp; Off-Platform Policy</h2>
            <ul>
              <li>
                <strong>Formation.</strong> A booking is formed when you accept
                a guest’s request or when the booking is auto-accepted through
                any instant-book or similar feature you enable.
              </li>
              <li>
                <strong>Host cancellations.</strong> Cancellations should be
                limited to necessary situations (safety concerns, force majeure,
                legal constraints, or clear policy violations). Excessive or
                late cancellations may result in fees, lower search ranking, or
                account action.
              </li>
              <li>
                <strong>Guest cancellations.</strong> Your listing must state a
                clear cancellation policy (for example, flexible, moderate,
                strict). The Platform applies that policy when determining
                refunds, subject to any overriding protections stated in the
                Platform Terms (for example, guest safety issues).
              </li>
              <li>
                <strong>On-Platform only.</strong> All booking-related
                communications and payments for guests introduced through the
                Platform must stay on the Platform. Circumventing the Platform
                (for example, taking bookings off-site to avoid fees) is not
                allowed and may result in account action.
              </li>
            </ul>
          </section>

          <section id="access" data-anchor>
            <h2>9. Access, Condition, Safety &amp; Recording Devices</h2>
            <ul>
              <li>
                <strong>Access.</strong> You will provide clear and timely
                instructions for check-in, check-out, and access (including keys
                or codes).
              </li>
              <li>
                <strong>Maintenance &amp; emergencies.</strong> You retain the
                right to enter for emergencies, necessary repairs, or compliance
                with law and house rules, subject to any applicable notice
                requirements.
              </li>
              <li>
                <strong>Safety plan.</strong> You will keep exits and safety
                equipment unobstructed and provide basic emergency contacts and
                instructions.
              </li>
              <li>
                <strong>Recording devices.</strong> You must disclose any
                security cameras or recording devices in non-private areas in
                your listing and comply with all recording, wiretapping, notice,
                and consent laws. Devices may never be placed in private areas
                (such as bedrooms or bathrooms).
              </li>
            </ul>
          </section>

          <section id="damage" data-anchor>
            <h2>10. Damage, Deposits, Claims &amp; Costs</h2>
            <ul>
              <li>
                <strong>Security deposits.</strong> You may set a reasonable
                security deposit amount, and {BRAND} may facilitate holds or
                charges in line with the Platform Terms.
              </li>
              <li>
                <strong>Guest responsibility.</strong> Guests are responsible
                for damage, loss, or extra cleaning caused by them or their
                invitees, subject to applicable law.
              </li>
              <li>
                <strong>Claims process.</strong> You agree to promptly document
                any issues (for example, photos, incident descriptions,
                estimates) and submit claims within the applicable window.{" "}
                <strong>{BRAND} is not an insurer</strong> and does not
                guarantee reimbursement.
              </li>
              <li>
                <strong>Your costs.</strong> You are solely responsible for all
                costs associated with hosting and your property, including
                repairs, replacements, extra cleaning, pest remediation,
                nuisance fines, code/permit penalties, HOA or landlord charges,
                professional and legal fees, and any taxes that remain your
                responsibility.
              </li>
            </ul>
          </section>

          <section id="insurance" data-anchor>
            <h2>11. Insurance &amp; Waiver of Subrogation</h2>
            <p>
              You agree to maintain appropriate insurance for your space and
              activities (for example, commercial general liability, property
              and contents, event coverage, and short-term rental coverage, as
              applicable). Where permitted by your insurer, you will name{" "}
              {BRAND} as an additional insured for claims arising from your
              hosting activities and provide certificates upon request. To the
              fullest extent permitted by law, you waive—and will cause your
              insurers to waive—any rights of subrogation against {BRAND} and
              its affiliates.
            </p>
          </section>

          <section id="risk" data-anchor>
            <h2>12. Risk Allocation; Release, Indemnification &amp; Hold Harmless</h2>
            <p>
              Hosting carries inherent risks. To the maximum extent permitted by
              applicable law (including Missouri law), you agree as follows:
            </p>
            <ul>
              <li>
                <strong>Assumption of risk.</strong> You assume all risks
                arising from your listing, your space, and your hosting
                activities, including the conduct of guests and invitees.
              </li>
              <li>
                <strong>Release.</strong> You release {BRAND} and its
                affiliates, and their officers, directors, employees, and agents
                (collectively, the <strong>“{BRAND} Parties”</strong>) from any
                and all claims and liabilities arising from or relating to your
                hosting activities, your space, or guest conduct, except to the
                extent caused by the {BRAND} Parties’ willful misconduct.
              </li>
              <li>
                <strong>Indemnification &amp; hold harmless.</strong> You will{" "}
                <strong>defend, indemnify, and hold harmless</strong> the{" "}
                {BRAND} Parties from and against any and all claims, demands,
                actions, liabilities, losses, fines, penalties, judgments,
                damages (including damage to your own property), costs, and
                expenses (including reasonable attorneys’ fees) arising out of
                or related to: (a) your listings, space, or hosting activities
                (events or overnight stays); (b) property damage caused by you,
                your agents, or any guest or invitee; (c) personal injury at or
                around the space; (d) alleged violations of law, permits, HOA or
                landlord rules, or third-party rights; (e) your content; or (f)
                your breach of these Host Terms or the Platform Terms. This does
                not apply to the extent a claim is caused by the {BRAND} Parties’
                willful misconduct.
              </li>
              <li>
                <strong>Limitation of liability.</strong> The {BRAND} Parties
                will not be liable for any indirect, incidental, special,
                consequential, exemplary, or punitive damages, or for lost
                profits or loss of goodwill. The {BRAND} Parties’ aggregate
                liability arising out of or relating to hosting will not exceed
                the greater of (i) USD $1,000 or (ii) the amounts paid to you
                via the Platform for the booking(s) giving rise to the claim in
                the three (3) months immediately before the event first giving
                rise to liability, in each case to the fullest extent permitted
                by law.
              </li>
            </ul>
          </section>

          <section id="checks" data-anchor>
            <h2>13. KYC, Background &amp; Compliance Checks</h2>
            <p>
              Subject to law, we may conduct identity verification, sanctions
              screening, licensing checks, or other due diligence on you or your
              space, and request documentation to verify compliance. We may
              disable or remove listings or take account action where risk or
              non-compliance is suspected.
            </p>
          </section>

          <section id="content" data-anchor>
            <h2>14. Reviews &amp; Content License</h2>
            <p>
              You retain ownership of your content, but you grant {BRAND} a
              worldwide, royalty-free, transferable, sublicensable license to
              use, host, reproduce, modify, adapt, translate, distribute,
              publicly display, and promote that content to operate and market
              the Platform and your listing. You represent that you have all
              rights needed to grant this license. We may moderate reviews and
              content for policy or legal violations.
            </p>
          </section>

          <section id="data" data-anchor>
            <h2>15. Data, Communications &amp; Electronic Signatures</h2>
            <ul>
              <li>
                <strong>Communications.</strong> You consent to receive
                Platform-related notices electronically, including via email,
                SMS (where enabled), and in-product messages.
              </li>
              <li>
                <strong>Electronic signatures.</strong> You agree that clicks,
                taps, toggles, and other electronic actions (including checking
                boxes and tapping “Agree” or similar) constitute your
                electronic signature and agreement to these Host Terms.
              </li>
              <li>
                <strong>Privacy.</strong> We process personal data in accordance
                with our{" "}
                <Link to="/legal/privacy" className="underline">
                  Privacy Policy
                </Link>
                .
              </li>
            </ul>
          </section>

          <section id="suspension" data-anchor>
            <h2>16. Suspension &amp; Termination</h2>
            <p>
              We may suspend or terminate your hosting privileges or listings
              where we reasonably believe there is a policy violation, safety
              concern, fraud risk, legal risk, or other material issue. You may
              close your account at any time. Provisions that by their nature
              should survive (including Sections 5–12, 16, 18–19) will survive
              termination.
            </p>
          </section>

          <section id="changes" data-anchor>
            <h2>17. Modifications</h2>
            <p>
              We may update these Host Terms by posting a revised version with
              an updated Effective Date. Where reasonably possible, we will
              provide notice of material changes. Your continued use of the
              Platform as a Host after changes take effect constitutes your
              acceptance of the updated Host Terms.
            </p>
          </section>

          <section id="law" data-anchor>
            <h2>18. Governing Law (Missouri); Arbitration; Venue; Class Action Waiver</h2>
            <ul>
              <li>
                <strong>Governing law.</strong> These Host Terms and any dispute
                between you and {BRAND} are governed by the laws of the State of
                Missouri, without regard to its conflict-of-law principles.
              </li>
              <li>
                <strong>Arbitration.</strong> Any dispute, claim, or controversy
                arising out of or relating to these Host Terms, the Platform,
                your listing, or your hosting activities shall be resolved by
                binding arbitration administered by the American Arbitration
                Association (AAA) under its Commercial Arbitration Rules. The
                seat and venue of arbitration shall be{" "}
                <strong>St. Louis County, Missouri</strong>. One arbitrator.
                Judgment on the award may be entered in any court of competent
                jurisdiction.
              </li>
              <li>
                <strong>Injunctive relief &amp; small claims.</strong> Either
                party may seek provisional injunctive or equitable relief in
                state or federal courts located in{" "}
                <strong>St. Louis County, Missouri</strong>, and either party
                may pursue an individual claim in small claims court if
                eligible.
              </li>
              <li>
                <strong>Class action waiver.</strong> All proceedings must be
                conducted solely on an individual basis. No class, collective,
                consolidated, representative, or private-attorney-general
                actions are permitted.
              </li>
              <li>
                <strong>Jury trial waiver.</strong> To the extent a dispute is
                litigated in court rather than arbitrated, both parties waive
                any right to a jury trial to the fullest extent permitted by
                law.
              </li>
            </ul>
          </section>

          <section id="misc" data-anchor>
            <h2>19. Miscellaneous</h2>
            <ul>
              <li>
                <strong>Entire agreement.</strong> These Host Terms and the
                Platform Terms are the entire agreement between you and {BRAND}{" "}
                regarding hosting on the Platform and supersede any prior
                understandings.
              </li>
              <li>
                <strong>Assignment.</strong> You may not assign or transfer
                these Host Terms without our prior written consent. We may
                assign them in connection with a merger, acquisition, financing,
                or sale of assets, or by operation of law.
              </li>
              <li>
                <strong>Severability.</strong> If any provision of these Host
                Terms is found unenforceable, it will be enforced to the maximum
                extent permitted, and the remaining provisions will remain in
                full force and effect.
              </li>
              <li>
                <strong>No waiver.</strong> Failure to enforce any provision is
                not a waiver of that provision or any other.
              </li>
              <li>
                <strong>Headings.</strong> Section headings are for convenience
                only and do not affect interpretation.
              </li>
              <li>
                <strong>Interpretation.</strong> “Including” means “including
                without limitation.” References to the singular include the
                plural and vice versa.
              </li>
            </ul>
          </section>

          <section id="contact" data-anchor>
            <h2>20. Contact</h2>
            <p>
              Questions about these Host Terms? Contact us at{" "}
              <a
                className="underline"
                href="mailto:support@yourewelcome.example"
              >
                support@yourewelcome.example
              </a>
              .
            </p>
          </section>

          {/* Bottom Reached Helper */}
          <div className="not-prose mt-10 flex items-center justify-between rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm">
            <div className={atEnd ? "text-green-700" : "text-neutral-600"}>
              {atEnd
                ? "Bottom reached — you can return to your account to accept."
                : "Scroll to the very bottom to confirm you've read these terms."}
            </div>
            {returnHref && (
              <a
                className="text-sm font-medium text-[#7C9082] underline underline-offset-2 hover:text-[#6C7F72]"
                href={returnHref}
              >
                Return to Account
              </a>
            )}
          </div>

          {/* Legal Template Disclaimer (visible) */}
          <p className="not-prose mt-6 text-[11px] leading-relaxed text-neutral-500">
            This template is for general informational purposes only and is not
            legal or tax advice. You should have a Missouri-licensed attorney
            review and adapt these Host Terms for your specific situation before
            relying on them.
          </p>
        </main>
      </div>
    </div>
  );
}
